import { Button } from "@/v2-components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/v2-components/ui/dialog";
import { useDeleteGameItems, useDeleteItem } from "@/store/cart-store";
import { useTrans } from "@/utils/useTrans";
import { useCartWithProducts } from "@/store/useCartWithProducts";
import { useMemo } from "react";
import { useCurrency } from "@/hooks/useCurrency";
import { useLocale } from "@/store/locale-store";
import { ProductSlug } from "@/api/product-api";
import React from "react";
import { OrderItemV2 } from "@/models/orders/OrderDraft";
import { GameItem, GameItemState } from "@/models/orders/OrderModelV2";
import { OrderSummaryGameItems } from "@/v2-components/pages/checkout/order-summary-game-items";
import { OrderSummaryDeviceItems } from "@/v2-components/pages/checkout/order-summary-device-items";

export default function AlertUnavailableBagItems() {
  // 게임 줄의 가격 통화는 `useCartWithProducts` 의 판정이 정한다(전역 `useCurrency` — 결제와 같은 축).
  const cartWithProducts = useCartWithProducts();
  const { countryCode } = useLocale();
  const deleteItem = useDeleteItem();
  const deleteGameItems = useDeleteGameItems();

  // ★게임 줄은 **판매 불가가 확인된 것만** 지운다. 판정은 장바구니 행 · 결제 목록 · 결제 게이트와 같은
  // 함수(`@/libs/cart/cart-game-stock` — `useCartWithProducts().unavailableGameLines`)다.
  //
  // 키 재고를 모르는 줄(`checking` — mgmt 타임아웃 · 배치 조회 실패 · 판매 목록 미로드)은 여기 오지 않는다. 종전엔
  // 「모름」도 품절로 쳐서 이 다이얼로그를 띄우고 **닫는 순간 지웠다** — 일시 장애가 장바구니를 비웠다. 지금은 행에
  // 「재고 확인 중」으로 남고 결제만 막힌다. 그래서 다이얼로그는 로딩을 기다릴 필요도 없다 — 「모름」은 판매 불가가
  // 아니므로, 판매 목록 · 재고 응답이 와야만 줄이 여기로 온다.
  const unavailableGameItems = useMemo(() => {
    // 국가를 고르기 전엔 띄우지 않는다(종전 동작). 결제는 국가 선택을 먼저 요구한다.
    if (!countryCode) return [];
    return cartWithProducts.unavailableGameLines.map((line) => {
      // ⚠️ 판매 가능 항목(`gameItem`)은 **해당 통화 가격이 없으면 null** 이다(#5179 §4) — 그런데 가격 없음
      // (`no-price`)이 바로 이 안내의 사유 중 하나라, 그것만 쓰면 **다이얼로그는 뜨는데 본문이 빈다**.
      // 여기서 필요한 건 "무엇이 지워지는지" 뿐이므로, 가격을 못 구해도 이름·썸네일로 표시용 항목을 만든다.
      // (가격 0 은 표시에 쓰이지 않고 삭제 대상 안내 전용 — 판매 목록에 없는 줄은 보여줄 게 없어 지우기만 한다)
      const { resell } = line;
      const gameItem: GameItem | null =
        line.gameItem ??
        (resell
          ? {
              state: GameItemState.Prepared,
              platform: line.item.platform,
              discountPercentile: 0,
              compatibleDevices: resell.compatibleDevices,
              price: 0,
              discount: 0,
              name: resell.name,
              id: resell.contentId,
              gameResellId: resell.id,
              thumbnail: resell.thumbnailPortraitImageLink,
              thumbnailHorizontal: resell.thumbnailImageLink,
              disableCountryList: resell.disableCountryList,
            }
          : null);
      return { index: line.index, gameItem };
    });
  }, [countryCode, cartWithProducts.unavailableGameLines]);

  const unavailableDeviceItems = useMemo(() => {
    const unavailableBundleItems =
      countryCode === "KR"
        ? cartWithProducts.orderItems.filter((i) =>
            [ProductSlug.METRO_BUNDLE, ProductSlug.ARIZONA_BUNDLE].includes(
              i.slug as any,
            ),
          )
        : [];

    const soldOutItems = cartWithProducts.orderItems.filter((orderItem) => {
      const product = cartWithProducts.items.find(
        (i) => i.sku === orderItem.sku,
      );
      const skuOption = product?.skuOptions.find(
        (opt) => opt.sku === orderItem.sku,
      );
      return skuOption?.stock === 0;
    });

    return [...soldOutItems, ...unavailableBundleItems];
  }, [cartWithProducts.orderItems, cartWithProducts.items, countryCode]);

  const isOpen =
    unavailableGameItems.length > 0 || unavailableDeviceItems.length > 0;

  return (
    <DialogUnavailableBagItems
      open={isOpen}
      gameItems={unavailableGameItems
        .map((item) => item.gameItem)
        .filter((item): item is GameItem => !!item)}
      deviceItems={unavailableDeviceItems}
      onOpenChange={(open) => {
        if (!open) {
          const gameItemIndexes = unavailableGameItems.map(
            (item) => item.index,
          );
          deleteGameItems(gameItemIndexes);
          unavailableDeviceItems.forEach((item) => {
            deleteItem(item.sku);
          });
        }
      }}
    />
  );
}

const DialogUnavailableBagItems = ({
  gameItems = [],
  deviceItems = [],
  onOpenChange,
  open,
}: {
  open: boolean;
  deviceItems: OrderItemV2[];
  gameItems: GameItem[];
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTrans(["gnb"]);
  const totalItems = gameItems.length + deviceItems.length;
  const currency = useCurrency();
  const { countryCode } = useLocale();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] min-h-[200px] max-w-[460px] flex-col p-8">
        <DialogHeader>
          <DialogTitle>{t("Unavailable Item")}</DialogTitle>
          <DialogClose />
        </DialogHeader>
        <DialogDescription className="text-red-60">
          {t(
            "Some items in your bag are not available for purchase in the selected country. They will be removed from your bag.",
          )}
        </DialogDescription>

        {totalItems > 0 && (
          <div className="mt-6 flex-1 overflow-y-auto">
            {!!deviceItems.length && (
              <OrderSummaryDeviceItems
                currency={currency}
                countryCode={countryCode}
                items={deviceItems}
              />
            )}
            {!!gameItems.length && (
              <OrderSummaryGameItems
                gameItems={gameItems}
                currency={currency}
                countryCode={countryCode}
              />
            )}
          </div>
        )}

        <DialogFooter className="w-full items-center justify-end gap-2">
          <Button
            size="sm"
            variant="blue-fill"
            className="px-7"
            shape="pill"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t("Confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
